import React, { useEffect, useRef, useState } from "react";
import { fetchAppointments } from "./calendarApi";
import CalendarPrintSchedule from "./CalendarPrintSchedule";

const pad2 = (value) => String(value).padStart(2, "0");

const todayValue = () => {
  const today = new Date();
  return `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;
};

const daysInMonth = (year, month) => {
  if (month === 2) return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
};

const validDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return year >= 1 && month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month);
};

const readRange = ({ mode, singleDate, monthValue, fromDate, toDate }) => {
  if (mode === "day") {
    if (!validDate(singleDate)) throw new Error("Select a valid date.");
    return { start: singleDate, end: singleDate };
  }
  if (mode === "month") {
    const match = /^(\d{4})-(\d{2})$/.exec(String(monthValue || ""));
    if (!match) throw new Error("Select a valid month and year.");
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (year < 1 || month < 1 || month > 12) throw new Error("Select a valid month and year.");
    return {
      start: `${match[1]}-${match[2]}-01`,
      end: `${match[1]}-${match[2]}-${pad2(daysInMonth(year, month))}`,
    };
  }
  if (!validDate(fromDate) || !validDate(toDate)) throw new Error("Select valid FROM and TO dates.");
  if (fromDate > toDate) throw new Error("FROM date cannot be later than TO date.");
  return { start: fromDate, end: toDate };
};

const formatRangeLabel = (start, end) => {
  const format = (value) => {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };
  return start === end ? format(start) : `${format(start)} — ${format(end)}`;
};

const CalendarPrintDialog = ({ isOpen, onClose, businessName }) => {
  const today = useRef(todayValue()).current;
  const [mode, setMode] = useState("day");
  const [singleDate, setSingleDate] = useState(today);
  const [monthValue, setMonthValue] = useState(today.slice(0, 7));
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [printData, setPrintData] = useState(null);
  const requestGeneration = useRef(0);
  const requestController = useRef(null);

  const invalidateRequest = () => {
    requestGeneration.current += 1;
    requestController.current?.abort();
    requestController.current = null;
  };

  useEffect(() => () => {
    requestGeneration.current += 1;
    requestController.current?.abort();
  }, []);

  const closeDialog = () => {
    invalidateRequest();
    setLoading(false);
    setError("");
    setPrintData(null);
    onClose();
  };

  const changeSelection = (setter) => (event) => {
    setter(event.target.value);
    setError("");
    setPrintData(null);
  };

  const printSchedule = async () => {
    if (loading) return;
    let range;
    try {
      range = readRange({ mode, singleDate, monthValue, fromDate, toDate });
    } catch (validationError) {
      setError(validationError.message);
      setPrintData(null);
      return;
    }

    invalidateRequest();
    const generation = requestGeneration.current;
    const controller = new AbortController();
    requestController.current = controller;
    setLoading(true);
    setError("");
    setPrintData(null);
    try {
      const appointments = await fetchAppointments({
        start: range.start,
        end: range.end,
        signal: controller.signal,
      });
      if (generation !== requestGeneration.current) return;
      setPrintData({ appointments, rangeLabel: formatRangeLabel(range.start, range.end) });
      setLoading(false);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (generation === requestGeneration.current) window.print();
        });
      });
    } catch (requestError) {
      if (requestError?.name === "AbortError" || generation !== requestGeneration.current) return;
      setError(requestError?.message || "Unable to load the appointment schedule.");
      setLoading(false);
      setPrintData(null);
    } finally {
      if (generation === requestGeneration.current) requestController.current = null;
    }
  };

  if (!isOpen) return null;

  const optionStyle = { display: "flex", gap: 9, alignItems: "center", minHeight: 40, fontWeight: 900 };
  const inputStyle = { width: "100%", minHeight: 44, marginTop: 6, padding: "7px 10px", border: "3px solid #000", borderRadius: 8, boxSizing: "border-box", fontWeight: 800 };
  const buttonStyle = { minHeight: 46, padding: "8px 16px", border: "3px solid #000", borderRadius: 10, fontWeight: 900, cursor: loading ? "wait" : "pointer" };

  return (
    <>
      <div style={{ position: "fixed", inset: 0, zIndex: 10030, display: "grid", placeItems: "center", padding: 12, background: "rgba(0,0,0,0.68)", boxSizing: "border-box" }} onMouseDown={closeDialog}>
        <div role="dialog" aria-modal="true" aria-labelledby="calendar-print-title" onMouseDown={(event) => event.stopPropagation()} style={{ width: "100%", maxWidth: 480, maxHeight: "calc(100vh - 24px)", overflowY: "auto", padding: 16, border: "6px solid #000", borderRadius: 14, background: "#E8E2DA", color: "#000", boxSizing: "border-box" }}>
          <h2 id="calendar-print-title" style={{ margin: "0 0 12px", fontSize: 22 }}>PRINT APPOINTMENT SCHEDULE</h2>

          <label style={optionStyle}>
            <input type="radio" name="calendar-print-mode" value="day" checked={mode === "day"} disabled={loading} onChange={changeSelection(setMode)} />
            SINGLE DAY
          </label>
          {mode === "day" ? <input type="date" aria-label="Single day" value={singleDate} disabled={loading} onChange={changeSelection(setSingleDate)} style={inputStyle} /> : null}

          <label style={{ ...optionStyle, marginTop: 10 }}>
            <input type="radio" name="calendar-print-mode" value="month" checked={mode === "month"} disabled={loading} onChange={changeSelection(setMode)} />
            ENTIRE MONTH
          </label>
          {mode === "month" ? <input type="month" aria-label="Month and year" value={monthValue} disabled={loading} onChange={changeSelection(setMonthValue)} style={inputStyle} /> : null}

          <label style={{ ...optionStyle, marginTop: 10 }}>
            <input type="radio" name="calendar-print-mode" value="range" checked={mode === "range"} disabled={loading} onChange={changeSelection(setMode)} />
            CUSTOM DATE RANGE
          </label>
          {mode === "range" ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <label style={{ fontWeight: 900 }}>FROM<input type="date" value={fromDate} disabled={loading} onChange={changeSelection(setFromDate)} style={inputStyle} /></label>
              <label style={{ fontWeight: 900 }}>TO<input type="date" value={toDate} disabled={loading} onChange={changeSelection(setToDate)} style={inputStyle} /></label>
            </div>
          ) : null}

          {loading ? <div role="status" style={{ marginTop: 12, fontWeight: 900 }}>LOADING APPOINTMENTS...</div> : null}
          {!loading && error ? <div role="alert" style={{ marginTop: 12, padding: 10, border: "3px solid #000", background: "#fee2e2", fontWeight: 900 }}>{error}</div> : null}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
            <button type="button" disabled={loading} onClick={printSchedule} style={{ ...buttonStyle, flex: 1, background: "#000", color: "#E8C98F", borderColor: "#E8761D" }}>PRINT SCHEDULE</button>
            <button type="button" onClick={closeDialog} style={{ ...buttonStyle, flex: 1, background: "#fff", color: "#000", cursor: "pointer" }}>CANCEL</button>
          </div>
        </div>
      </div>

      {printData ? <CalendarPrintSchedule appointments={printData.appointments} businessName={businessName} rangeLabel={printData.rangeLabel} /> : null}
    </>
  );
};

export default CalendarPrintDialog;
